import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Skeleton } from '@mui/material';
import Slider from 'react-slick';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import YouTube from 'react-youtube';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useDevice } from '../hooks/useResponsive';

export default function YoutubeShortsCarousalSection({ YoutubeShorts, productSingle, mb }) {
  const { isMobile } = useDevice();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [invalidIds, setInvalidIds] = useState({});
  const [loadingVideos, setLoadingVideos] = useState({}); // track loading state per video
  const [isPlayingMap, setIsPlayingMap] = useState({}); // track playing state per video
  const sliderRef = React.useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const validateVideos = async () => {
      if (!YoutubeShorts?.length) return;

      const checks = await Promise.all(
        YoutubeShorts.map(async (video) => {
          const id = video?.video_id;
          if (!id) return null;
          const isValid = await isYouTubeVideoValid(id);
          return !isValid ? id : null;
        })
      );

      // Filter out nulls and build result object
      const result = checks.reduce((acc, id) => {
        if (id) acc[id] = true;
        return acc;
      }, {});

      setInvalidIds(result);
    };

    validateVideos();
  }, [YoutubeShorts]);

  const isYouTubeVideoValid = async (videoId) => {
    const url = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
    try {
      const res = await fetch(url);
      return res.ok;
    } catch {
      return false;
    }
  };

  const handleDragStart = useCallback((e) => {
    if (!sliderRef.current) return;

    setIsDragging(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragStart({ x: clientX, y: clientY });
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragMove = useCallback(
    (e) => {
      if (!isDragging || !sliderRef.current) return;

      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const deltaX = clientX - dragStart.x;
      const threshold = 50; // Minimum drag distance

      if (Math.abs(deltaX) > threshold) {
        if (deltaX > 0) {
          // Drag right - go to previous slide
          sliderRef.current.slickPrev();
        } else {
          // Drag left - go to next slide
          sliderRef.current.slickNext();
        }
        setIsDragging(false);
      }
    },
    [isDragging, dragStart]
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse and touch events for drag handling
  useEffect(() => {
    const handleMouseMove = (e) => handleDragMove(e);
    const handleMouseUp = () => handleDragEnd();
    const handleTouchMove = (e) => handleDragMove(e);
    const handleTouchEnd = () => handleDragEnd();

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  const opts = {
    height: isMobile ? '220px' : '390px',
    host: 'https://www.youtube-nocookie.com',
    playerVars: {
      autoplay: 0,
      rel: 0, // Hide related videos from other channels (may still show same-channel)
      modestbranding: 1, // Reduce YouTube branding
      controls: 0, // Hide all player controls/icons
      fs: 0, // Hide fullscreen button
      iv_load_policy: 3, // Hide video annotations
      cc_load_policy: 0, // Disable closed captions by default
      disablekb: 1, // Disable keyboard controls
      playsinline: 1, // Play inline on iOS
    },
  };

  // custom arrows same as yours...
  const CustomPrevArrow = ({ onClick, currentSlide }) => {
    if (currentSlide === 0) return null;
    return (
      <Stack
        role="button"
        tabIndex="0"
        onClick={onClick}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
        sx={{
          backgroundColor: 'rgba(100, 100, 100, 0.9)',
          borderRadius: '50%',
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          position: 'absolute',
          left: isMobile ? 0 : '15px',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          '&:hover': { backgroundColor: 'rgba(80, 80, 80, 0.9)' },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 30, color: 'white' }} />
      </Stack>
    );
  };
  CustomPrevArrow.propTypes = { onClick: PropTypes.func, currentSlide: PropTypes.number.isRequired };
  const CustomNextArrow = ({ onClick, currentSlide, slideCount, slidesToShow }) => {
    if (currentSlide >= slideCount - slidesToShow) return null;
    return (
      <Stack
        role="button"
        tabIndex="0"
        onClick={onClick}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
        sx={{
          backgroundColor: 'rgba(100, 100, 100, 0.9)',
          borderRadius: '50%',
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          '&:hover': { backgroundColor: 'rgba(80, 80, 80, 0.9)' },
        }}
      >
        <ChevronRightIcon sx={{ fontSize: 30, color: 'white' }} />
      </Stack>
    );
  };
  CustomNextArrow.propTypes = {
    onClick: PropTypes.func,
    currentSlide: PropTypes.number.isRequired,
    slideCount: PropTypes.number.isRequired,
    slidesToShow: PropTypes.number.isRequired,
  };
  const settings = {
    dots: true,
    infinite: false,
    autoplay: false,
    slidesToShow: 5.7,
    slidesToScroll: 1,
    speed: 500,
    arrows: true,
    draggable: true, // Enable dragging
    swipeToSlide: true, // Enable swipe to slide
    touchMove: true, // Enable touch movement
    prevArrow: <CustomPrevArrow currentSlide={currentSlide} />,
    nextArrow: (
      <CustomNextArrow
        currentSlide={currentSlide}
        slideCount={YoutubeShorts.length}
        slidesToShow={isMobile ? 1.1 : 1.3}
      />
    ),
    afterChange: (index) => setCurrentSlide(index),
    responsive: [
      { breakpoint: 425, settings: { slidesToShow: 1.5 } },
      { breakpoint: 600, settings: { slidesToShow: 2.7 } },
      { breakpoint: 960, settings: { slidesToShow: 3.7 } },
      { breakpoint: 1920, settings: { slidesToShow: 5.7 } },
    ],
  };

  return (
    <Stack
      className={`shorts-carousal-home ${mb ? 'new-mb' : ''}`}
      sx={{
        maxWidth: '100%',
        pl: { xs: 2, md: productSingle ? '60px' : '71px' },
        pt: isMobile ? 4 : 11,
        '& .ytm-custom-control .player-controls-top': {
          display: 'none',
        },
      }}
    >
      <Box
        sx={{
          maxWidth: '100%',
          px: 0,
          pb: productSingle ? 9 : 4,
          '& .slick-dots': {
            display: 'flex !important',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '6px',
            bottom: '-30px',
          },
          '& .slick-dots li button:before': { display: 'none' },
        }}
      >
        <Slider ref={sliderRef} {...settings} className="youtube-carousal">
          {YoutubeShorts.map((video, index) => {
            const isInvalid = invalidIds[video?.video_id];

            return (
              <Box
                key={index}
                className={`react-player-shorts react-youtube-player yt-short-player-${index}`}
                sx={{
                  position: 'relative',
                  pl: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  // height: opts.height,
                  color: '#fff',
                  height: '100%',
                }}
              >
                {!isInvalid ? (
                  <>
                    {/* Drag Handle - Only show when video is NOT playing Top */}
                    {!isPlayingMap[video?.video_id] && (
                      <Box
                        className="ameen"
                        sx={{
                          position: 'absolute',
                          left: '0px',
                          right: '0px',
                          top: 0,
                          height: '40%',
                          zIndex: 2,
                          cursor: 'grab',
                          '&:active': {
                            cursor: 'grabbing',
                          },
                          '&:hover': {
                            // backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            '&::after': {
                              content: '"⋯"',
                              position: 'absolute',
                              left: '50%',
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: 'white',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              opacity: 0.7,
                            },
                          },
                        }}
                        onMouseDown={handleDragStart}
                        onTouchStart={handleDragStart}
                      />
                    )}
                    {/* bottom */}
                    {!isPlayingMap[video?.video_id] && (
                      <Box
                        className="ameen"
                        sx={{
                          position: 'absolute',
                          left: '0px',
                          right: '0px',
                          bottom: 0,
                          height: '45%',
                          zIndex: 2,
                          cursor: 'grab',
                          '&:active': {
                            cursor: 'grabbing',
                          },
                          '&:hover': {
                            // backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            '&::after': {
                              content: '"⋯"',
                              position: 'absolute',
                              left: '50%',
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: 'white',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              opacity: 0.7,
                            },
                          },
                        }}
                        onMouseDown={handleDragStart}
                        onTouchStart={handleDragStart}
                      />
                    )}

                    <YouTube
                      videoId={video?.video_id}
                      opts={opts}
                      className="react-player-shorts-video"
                      iframeClassName={isMobile ? 'w-90' : ''}
                      onReady={() => setLoadingVideos((prev) => ({ ...prev, [video?.video_id]: false }))}
                      onStateChange={(e) => {
                        const state = e?.data;
                        const isPlaying = state === 1; // 1 = playing
                        setIsPlayingMap((prev) => ({ ...prev, [video?.video_id]: isPlaying }));
                      }}
                      onPlay={() => {
                        document.querySelectorAll('.react-youtube-player iframe').forEach((iframe) => {
                          if (iframe !== document.querySelector(`.yt-short-player-${index} iframe`)) {
                            iframe.contentWindow.postMessage(
                              JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
                              '*'
                            );
                          }
                        });
                      }}
                    />
                    {loadingVideos[video?.video_id] !== false && (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: isMobile ? '95%' : '92%',
                          height: isMobile ? '218%' : '109%',
                          borderRadius: 2,
                          zIndex: 1,
                          marginLeft: '15px',
                        }}
                      />
                    )}
                  </>
                ) : (
                  <Box
                    className="react-player-shorts-video"
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      color: '#ccc',
                      border: '1px solid #555',
                      borderRadius: '15px',
                      minHeight: '420px',
                    }}
                  >
                    Invalid video ID: {video?.video_id}
                  </Box>
                )}
              </Box>
            );
          })}
        </Slider>
      </Box>
    </Stack>
  );
}

YoutubeShortsCarousalSection.propTypes = {
  YoutubeShorts: PropTypes.any,
  productSingle: PropTypes.any,
  mb: PropTypes.string,
};
